import type { WorkspaceTabsPosition } from 'src/models/workspaceTabs';
import type { PrimaryColorName, SurfaceColorName } from 'src/theme/themeConfig';

import { resolveStoredAppFontSizePt } from 'src/stores/userInterfaceSettings/appFontSize';
import {
	DetailToolbarButtonColors,
	resolveStoredDetailToolbarButtonColors,
} from 'src/stores/userInterfaceSettings/detailToolbarButtonColors';
import {
	APP_FONT_SIZE_PERCENT_KEY,
	APP_FONT_SIZE_PT_KEY,
	APP_LANGUAGE_KEY,
	ARCHIVED_VISIBLE_IN_LISTS_KEY,
	AVATAR_MODE_IN_LIST_KEY,
	BOTTOM_LEFT_MAIN_TOOLBAR_SLOT_KEY,
	BOTTOM_RIGHT_MAIN_TOOLBAR_SLOT_KEY,
	CACHING_ENABLED,
	COMPACT_BUTTON_VIEW_KEY,
	CONTROLS_MODE_KEY,
	DEFAULT_LIST_PER_MODULE_KEY,
	DETAIL_TOOLBAR_BUTTON_COLORS_KEY,
	DOCUMENT_PDF_PREVIEW_ZOOM_KEY,
	EDITOR_STANDARD_FONT_FAMILY_KEY,
	EDITOR_STANDARD_FONT_SIZE_KEY,
	FINISHED_TRIGGERS_BACK_TO_LIST_KEY,
	KANBAN_REMEMBER_LAST_BOARD_KEY,
	LIST_GROUPING_MODE_KEY,
	MENU_BOTTOM_SLOT_SORT_INVERSE_KEY,
	MENU_ITEMS_SOLID_KEY,
	MENU_ORDER_KEY,
	NAVIGATION_MODE_KEY,
	NOTIFICATIONS_APPOINTMENT_ENABLED,
	NOTIFICATIONS_DOCUMENTS_ENABLED,
	NOTIFICATIONS_IDO_ENABLED,
	NOTIFICATIONS_MAIL_ENABLED,
	NOTIFICATIONS_MESSAGE_ENABLED,
	NOTIFICATIONS_NOTES_ENABLED,
	NOTIFICATIONS_PHONE_CALLS_ENABLED,
	NOTIFICATIONS_TASKS_ENABLED,
	SELECTED_COLOR_SCHEME_FOR_THE_INSTANCE_KEY,
	SETUP_WIZARD_COMPLETED_KEY,
	SHARING_VISIBLE_IN_LISTS_KEY,
	SOFT_SHADOWS_KEY,
	SPLIT_VIEW_ENABLED,
	SPOTLIGHT_SEARCH_ENABLED,
	THEME_MODE_KEY,
	THEME_PRIMARY_COLOR_CUSTOM_KEY,
	THEME_PRIMARY_COLOR_KEY,
	THEME_SURFACE_COLOR_CUSTOM_KEY,
	THEME_SURFACE_COLOR_KEY,
	TIME_ZONE,
	TOP_LEFT_MAIN_TOOLBAR_SLOT_KEY,
	TOP_RIGHT_MAIN_TOOLBAR_SLOT_KEY,
	UNI_COLOR_BUTTONS_KEY,
	USER_ONLINE_LIST_VIEW_MODE,
	USER_ONLINE_POSITION_KEY,
	USER_ONLINE_SHOW_OFFLINE_USERS,
	WORKSPACE_TABS_CREATE_ACTIONS_IN_SPLIT_VIEW,
	WORKSPACE_TABS_ENABLED,
	WORKSPACE_TABS_POSITION,
	WORKSPACE_TABS_RECORDS_IN_SPLIT_VIEW,
} from 'src/stores/userInterfaceSettings/localStorageKeys';
import {
	DEFAULT_CUSTOM_PRIMARY_HEX,
	DEFAULT_CUSTOM_SURFACE_HEX,
	resolvePrimaryColorName,
	resolveStoredHexColor,
	resolveSurfaceColorName,
} from 'src/theme/themeConfig';
import { TimeZone } from 'timezones-list';

import {
	ConfigurableLayoutSlotButtons,
	IdoNotificationSubscription,
	MenuOrder,
	SelectedColorSchemeForTheInstance,
	Theme,
} from 'src/models/userInterfaceSettings';

import { ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY } from 'src/services/authentication';

import { type DateFormatKey, getDateFormatKey } from 'src/util/date/dateFormatOptions';
import { resolveStoredPdfPreviewZoom } from 'src/util/pdf/pdfPreviewSizing';
import { canUseDesktopPointerInteractions } from 'src/util/platform/browserInteractionCapabilities';

export type NavigationMode = 'sidebar' | 'topbar';

export type AppLanguage = 'de' | 'en' | 'system';

export interface UserInterfaceSettingsStateInterface {
	appFontSizePt: number;
	appLanguage: AppLanguage;
	archivedVisibleInLists: boolean;
	avatarModeInList: 'avatar' | 'avatarAndModule' | 'module';
	bottomLeftMainToolbarSlot: ConfigurableLayoutSlotButtons;
	bottomRightMainToolbarSlot: ConfigurableLayoutSlotButtons;
	colorSchemes: Array<Theme>;
	controlsMode: 'contextMenu' | 'swipeMenu';
	dateFormat: DateFormatKey;
	defaultListPerModule: Record<string, string>;
	detailToolbarButtonColors: DetailToolbarButtonColors;
	documentPdfPreviewZoom: null | number;
	editorStandardFontFamily: string;
	editorStandardFontSize: string;
	finishedTriggersBackToList: boolean;
	idoNotificationSubscriptions: IdoNotificationSubscription[];
	kanbanRememberLastBoard: boolean;
	listGroupingMode: 'moduleGrouping' | 'none' | 'timeFrameGrouping';
	menuBottomSlotSortInverse: boolean;
	menuItemsSolid: boolean;
	menuOrder: MenuOrder;
	navigationMode: NavigationMode;
	notificationsAppointmentEnabled: boolean;
	notificationsDocumentsEnabled: boolean;
	notificationsIdoEnabled: boolean;
	notificationsMailEnabled: boolean;
	notificationsMessageEnabled: boolean;
	notificationsNotesEnabled: boolean;
	notificationsPhoneCallsEnabled: boolean;
	notificationsTasksEnabled: boolean;
	performanceCachingEnabled: boolean;
	selectedColorSchemeForInstance: Array<SelectedColorSchemeForTheInstance>;
	setupWizardCompleted: boolean;
	sharingVisibleInLists: boolean;
	shouldUseSoftShadows: boolean;
	/**
	 * Sichtbarkeit der Button-Beschriftung in der Detail-Toolbar ("Button-Darstellung im Seitenpanel").
	 *
	 * Achtung, der Name sagt das Gegenteil des Werts: `true` zeigt die Beschriftung,
	 * `false` schaltet auf reine Icons. Die Toolbar leitet ihren Kompaktmodus als
	 * `!compactButtonView` ab (siehe `useDetailToolbarContext`).
	 */
	shouldUseTheCompactButtonView: boolean;
	shouldUseUniColorButtons: boolean;
	splitViewEnabled: boolean;
	spotlightSearchEnabled: boolean;
	themeMode: 'auto' | 'dark' | 'light';
	themePrimaryColor: PrimaryColorName;
	themePrimaryColorCustom: string;
	themeSurfaceColor: SurfaceColorName;
	themeSurfaceColorCustom: string;
	timeZone: 'auto' | TimeZone;
	timerEnabled: boolean;
	topLeftMainToolbarSlot: Array<ConfigurableLayoutSlotButtons>;
	topRightMainToolbarSlot: Array<ConfigurableLayoutSlotButtons>;
	userOnlineListViewMode: 'compressed' | 'large';
	userOnlinePosition: 'left' | 'off' | 'right';
	userOnlineShowOfflineUsers: boolean;
	userPreferencesLoaded: boolean;
	widgetSidebarActiveWidgets: string[];
	widgetSidebarEnabled: boolean;
	workspaceTabsCreateActionsInSplitView: boolean;
	workspaceTabsEnabled: boolean;
	workspaceTabsPosition: WorkspaceTabsPosition;
	workspaceTabsRecordsInSplitView: boolean;
}

const defaultTopRightMainToolbarSlot = (): Array<ConfigurableLayoutSlotButtons> => [
	ConfigurableLayoutSlotButtons.SEARCH,
	ConfigurableLayoutSlotButtons.EMTPY_SLOT,
];

const omitUnavailableLayoutSlotButton = (
	configuredLayoutSlotButton: ConfigurableLayoutSlotButtons
): ConfigurableLayoutSlotButtons => {
	if (configuredLayoutSlotButton === ConfigurableLayoutSlotButtons.CONTACT_INFORMATION) {
		return ConfigurableLayoutSlotButtons.EMTPY_SLOT;
	}

	return configuredLayoutSlotButton;
};

const omitUnavailableLayoutSlotButtons = (
	configuredLayoutSlotButtons: Array<ConfigurableLayoutSlotButtons>
): Array<ConfigurableLayoutSlotButtons> => configuredLayoutSlotButtons.map(omitUnavailableLayoutSlotButton);

const resolveStoredDefaultListPerModule = (raw: null | string): Record<string, string> => {
	if (raw === null) {
		return {};
	}

	try {
		const parsed: unknown = JSON.parse(raw);
		if (parsed === null || typeof parsed !== 'object') {
			return {};
		}

		const sanitized: Record<string, string> = {};
		for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
			if (typeof value === 'string' && value.length > 0) {
				sanitized[key] = value;
			}
		}
		return sanitized;
	} catch {
		return {};
	}
};

const resolveWorkspaceTabsEnabled = (): boolean => {
	const storedWorkspaceTabsEnabled = localStorage.getItem(WORKSPACE_TABS_ENABLED);
	return storedWorkspaceTabsEnabled !== null ? /true/i.test(storedWorkspaceTabsEnabled) : true;
};

const resolveSplitViewEnabled = (): boolean => {
	if (resolveWorkspaceTabsEnabled() && canUseDesktopPointerInteractions()) {
		return true;
	}
	const storedSplitView = localStorage.getItem(SPLIT_VIEW_ENABLED);
	return storedSplitView !== null ? /true/i.test(storedSplitView) : true;
};

const resolveTopRightMainToolbarSlot = (): Array<ConfigurableLayoutSlotButtons> => {
	if (localStorage.getItem(TOP_RIGHT_MAIN_TOOLBAR_SLOT_KEY) === null) {
		return defaultTopRightMainToolbarSlot();
	}

	const storedSlotButtons = JSON.parse(
		localStorage.getItem(TOP_RIGHT_MAIN_TOOLBAR_SLOT_KEY) as string
	) as Array<ConfigurableLayoutSlotButtons>;

	return omitUnavailableLayoutSlotButtons(storedSlotButtons);
};

// Vertikale Positionen (left/right) wurden entfernt — alte persistierte Werte fallen auf 'top' zurück.
export const resolveWorkspaceTabsPosition = (storedPosition: null | string): WorkspaceTabsPosition => {
	return storedPosition === 'bottom' || storedPosition === 'top' ? storedPosition : 'top';
};

function createDefaultUserInterfaceSettingsState(): UserInterfaceSettingsStateInterface {
	const defaultMenuOrder: MenuOrder = {
		addOns: 18,
		agendaList: 11,
		createMenu: 3,
		documentList: 10,
		filterList: 5,
		folderExplorer: 7,
		inbox: 1,
		kanbanBoard: 2,
		logout: 19,
		mailList: 12,
		messageList: 14,
		noteList: 13,
		phoneCallList: 8,
		records: 6,
		settings: 17,
		taskList: 9,
		timer: 15,
		trashList: 16,
		userOnline: 4,
	};

	return {
		appFontSizePt: resolveStoredAppFontSizePt(
			localStorage.getItem(APP_FONT_SIZE_PT_KEY),
			localStorage.getItem(APP_FONT_SIZE_PERCENT_KEY)
		),
		appLanguage:
			localStorage.getItem(APP_LANGUAGE_KEY) === 'de' || localStorage.getItem(APP_LANGUAGE_KEY) === 'en'
				? (localStorage.getItem(APP_LANGUAGE_KEY) as AppLanguage)
				: 'system',
		archivedVisibleInLists:
			localStorage.getItem(ARCHIVED_VISIBLE_IN_LISTS_KEY) !== null
				? /true/i.test(localStorage.getItem(ARCHIVED_VISIBLE_IN_LISTS_KEY) as 'false' | 'true')
				: true,
		avatarModeInList:
			localStorage.getItem(AVATAR_MODE_IN_LIST_KEY) !== null
				? (localStorage.getItem(AVATAR_MODE_IN_LIST_KEY) as 'avatar' | 'avatarAndModule' | 'module')
				: 'avatarAndModule',
		bottomLeftMainToolbarSlot:
			localStorage.getItem(BOTTOM_LEFT_MAIN_TOOLBAR_SLOT_KEY) !== null
				? omitUnavailableLayoutSlotButton(
						JSON.parse(
							localStorage.getItem(BOTTOM_LEFT_MAIN_TOOLBAR_SLOT_KEY) as string
						) as ConfigurableLayoutSlotButtons
					)
				: ConfigurableLayoutSlotButtons.USER_ONLINE,
		bottomRightMainToolbarSlot:
			localStorage.getItem(BOTTOM_RIGHT_MAIN_TOOLBAR_SLOT_KEY) !== null
				? omitUnavailableLayoutSlotButton(
						JSON.parse(
							localStorage.getItem(BOTTOM_RIGHT_MAIN_TOOLBAR_SLOT_KEY) as string
						) as ConfigurableLayoutSlotButtons
					)
				: ConfigurableLayoutSlotButtons.CREATE,
		colorSchemes: [],
		controlsMode:
			localStorage.getItem(CONTROLS_MODE_KEY) !== null
				? (localStorage.getItem(CONTROLS_MODE_KEY) as 'contextMenu' | 'swipeMenu')
				: 'contextMenu',
		dateFormat: getDateFormatKey(),
		defaultListPerModule: resolveStoredDefaultListPerModule(localStorage.getItem(DEFAULT_LIST_PER_MODULE_KEY)),
		detailToolbarButtonColors: resolveStoredDetailToolbarButtonColors(
			localStorage.getItem(DETAIL_TOOLBAR_BUTTON_COLORS_KEY)
		),
		documentPdfPreviewZoom: resolveStoredPdfPreviewZoom(localStorage.getItem(DOCUMENT_PDF_PREVIEW_ZOOM_KEY)),
		editorStandardFontFamily: localStorage.getItem(EDITOR_STANDARD_FONT_FAMILY_KEY) ?? '',
		editorStandardFontSize: localStorage.getItem(EDITOR_STANDARD_FONT_SIZE_KEY) ?? '',

		finishedTriggersBackToList:
			localStorage.getItem(FINISHED_TRIGGERS_BACK_TO_LIST_KEY) !== null
				? /true/i.test(localStorage.getItem(FINISHED_TRIGGERS_BACK_TO_LIST_KEY) as 'false' | 'true')
				: true,
		idoNotificationSubscriptions: [],
		kanbanRememberLastBoard:
			localStorage.getItem(KANBAN_REMEMBER_LAST_BOARD_KEY) !== null
				? /true/i.test(localStorage.getItem(KANBAN_REMEMBER_LAST_BOARD_KEY) as 'false' | 'true')
				: false,
		listGroupingMode:
			localStorage.getItem(LIST_GROUPING_MODE_KEY) !== null
				? (localStorage.getItem(LIST_GROUPING_MODE_KEY) as 'moduleGrouping' | 'none' | 'timeFrameGrouping')
				: 'moduleGrouping',
		menuBottomSlotSortInverse:
			localStorage.getItem(MENU_BOTTOM_SLOT_SORT_INVERSE_KEY) !== null
				? /true/i.test(localStorage.getItem(MENU_BOTTOM_SLOT_SORT_INVERSE_KEY) as 'false' | 'true')
				: true,
		menuItemsSolid:
			localStorage.getItem(MENU_ITEMS_SOLID_KEY) !== null
				? /true/i.test(localStorage.getItem(MENU_ITEMS_SOLID_KEY) as 'false' | 'true')
				: true,
		menuOrder:
			localStorage.getItem(MENU_ORDER_KEY) !== null
				? {
						...defaultMenuOrder,
						...(JSON.parse(localStorage.getItem(MENU_ORDER_KEY) as string) as Partial<MenuOrder>),
					}
				: defaultMenuOrder,
		navigationMode:
			localStorage.getItem(NAVIGATION_MODE_KEY) !== null
				? (localStorage.getItem(NAVIGATION_MODE_KEY) as NavigationMode)
				: 'sidebar',
		notificationsAppointmentEnabled:
			localStorage.getItem(NOTIFICATIONS_APPOINTMENT_ENABLED) !== null
				? /true/i.test(localStorage.getItem(NOTIFICATIONS_APPOINTMENT_ENABLED) as 'false' | 'true')
				: true,

		notificationsDocumentsEnabled:
			localStorage.getItem(NOTIFICATIONS_DOCUMENTS_ENABLED) !== null
				? /true/i.test(localStorage.getItem(NOTIFICATIONS_DOCUMENTS_ENABLED) as 'false' | 'true')
				: true,

		notificationsIdoEnabled:
			localStorage.getItem(NOTIFICATIONS_IDO_ENABLED) !== null
				? /true/i.test(localStorage.getItem(NOTIFICATIONS_IDO_ENABLED) as 'false' | 'true')
				: false,
		notificationsMailEnabled:
			localStorage.getItem(NOTIFICATIONS_MAIL_ENABLED) !== null
				? /true/i.test(localStorage.getItem(NOTIFICATIONS_MAIL_ENABLED) as 'false' | 'true')
				: false,
		notificationsMessageEnabled:
			localStorage.getItem(NOTIFICATIONS_MESSAGE_ENABLED) !== null
				? /true/i.test(localStorage.getItem(NOTIFICATIONS_MESSAGE_ENABLED) as 'false' | 'true')
				: true,
		notificationsNotesEnabled:
			localStorage.getItem(NOTIFICATIONS_NOTES_ENABLED) !== null
				? /true/i.test(localStorage.getItem(NOTIFICATIONS_NOTES_ENABLED) as 'false' | 'true')
				: true,
		notificationsPhoneCallsEnabled:
			localStorage.getItem(NOTIFICATIONS_PHONE_CALLS_ENABLED) !== null
				? /true/i.test(localStorage.getItem(NOTIFICATIONS_PHONE_CALLS_ENABLED) as 'false' | 'true')
				: true,
		notificationsTasksEnabled:
			localStorage.getItem(NOTIFICATIONS_TASKS_ENABLED) !== null
				? /true/i.test(localStorage.getItem(NOTIFICATIONS_TASKS_ENABLED) as 'false' | 'true')
				: true,
		performanceCachingEnabled:
			localStorage.getItem(CACHING_ENABLED) !== null
				? /true/i.test(localStorage.getItem(CACHING_ENABLED) as 'false' | 'true')
				: true,

		selectedColorSchemeForInstance:
			localStorage.getItem(SELECTED_COLOR_SCHEME_FOR_THE_INSTANCE_KEY) !== null
				? (JSON.parse(
						localStorage.getItem(SELECTED_COLOR_SCHEME_FOR_THE_INSTANCE_KEY) as string
					) as Array<SelectedColorSchemeForTheInstance>)
				: [
						{
							instance: localStorage.getItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY) as string,
							scheme: 'Blue Da Ba Dee',
						},
					],

		setupWizardCompleted: localStorage.getItem(SETUP_WIZARD_COMPLETED_KEY) === 'true',
		sharingVisibleInLists:
			localStorage.getItem(SHARING_VISIBLE_IN_LISTS_KEY) !== null
				? /true/i.test(localStorage.getItem(SHARING_VISIBLE_IN_LISTS_KEY) as 'false' | 'true')
				: true,

		shouldUseSoftShadows:
			localStorage.getItem(SOFT_SHADOWS_KEY) !== null
				? /true/i.test(localStorage.getItem(SOFT_SHADOWS_KEY) as 'false' | 'true')
				: false,

		shouldUseTheCompactButtonView:
			localStorage.getItem(COMPACT_BUTTON_VIEW_KEY) !== null
				? /true/i.test(localStorage.getItem(COMPACT_BUTTON_VIEW_KEY) as 'false' | 'true')
				: true,

		shouldUseUniColorButtons:
			localStorage.getItem(UNI_COLOR_BUTTONS_KEY) !== null
				? /true/i.test(localStorage.getItem(UNI_COLOR_BUTTONS_KEY) as 'false' | 'true')
				: true,

		splitViewEnabled: resolveSplitViewEnabled(),

		spotlightSearchEnabled:
			localStorage.getItem(SPOTLIGHT_SEARCH_ENABLED) !== null
				? /true/i.test(localStorage.getItem(SPOTLIGHT_SEARCH_ENABLED) as 'false' | 'true')
				: false,

		themeMode:
			localStorage.getItem(THEME_MODE_KEY) !== null
				? (localStorage.getItem(THEME_MODE_KEY) as 'auto' | 'dark' | 'light')
				: 'auto',

		themePrimaryColor: resolvePrimaryColorName(localStorage.getItem(THEME_PRIMARY_COLOR_KEY)),

		themePrimaryColorCustom: resolveStoredHexColor(
			localStorage.getItem(THEME_PRIMARY_COLOR_CUSTOM_KEY),
			DEFAULT_CUSTOM_PRIMARY_HEX
		),

		themeSurfaceColor: resolveSurfaceColorName(localStorage.getItem(THEME_SURFACE_COLOR_KEY)),

		themeSurfaceColorCustom: resolveStoredHexColor(
			localStorage.getItem(THEME_SURFACE_COLOR_CUSTOM_KEY),
			DEFAULT_CUSTOM_SURFACE_HEX
		),

		timeZone:
			localStorage.getItem(TIME_ZONE) !== null
				? (JSON.parse(localStorage.getItem(TIME_ZONE) as string) as 'auto' | TimeZone)
				: 'auto',

		timerEnabled: true,
		topLeftMainToolbarSlot:
			localStorage.getItem(TOP_LEFT_MAIN_TOOLBAR_SLOT_KEY) !== null
				? omitUnavailableLayoutSlotButtons(
						JSON.parse(
							localStorage.getItem(TOP_LEFT_MAIN_TOOLBAR_SLOT_KEY) as string
						) as Array<ConfigurableLayoutSlotButtons>
					)
				: [ConfigurableLayoutSlotButtons.MENU, ConfigurableLayoutSlotButtons.LOADING_INDICATOR],

		topRightMainToolbarSlot:
			localStorage.getItem(TOP_RIGHT_MAIN_TOOLBAR_SLOT_KEY) !== null
				? resolveTopRightMainToolbarSlot()
				: defaultTopRightMainToolbarSlot(),

		userOnlineListViewMode:
			localStorage.getItem(USER_ONLINE_LIST_VIEW_MODE) !== null
				? (localStorage.getItem(USER_ONLINE_LIST_VIEW_MODE) as 'compressed' | 'large')
				: 'large',

		userOnlinePosition:
			localStorage.getItem(USER_ONLINE_POSITION_KEY) !== null
				? (localStorage.getItem(USER_ONLINE_POSITION_KEY) as 'left' | 'off' | 'right')
				: 'left',

		userOnlineShowOfflineUsers:
			localStorage.getItem(USER_ONLINE_SHOW_OFFLINE_USERS) !== null
				? /true/i.test(localStorage.getItem(USER_ONLINE_SHOW_OFFLINE_USERS) as 'false' | 'true')
				: true,

		userPreferencesLoaded: false,

		widgetSidebarActiveWidgets: ['teamStatus', 'todayAppointments', 'dueTasks'],
		widgetSidebarEnabled: false,

		workspaceTabsCreateActionsInSplitView:
			localStorage.getItem(WORKSPACE_TABS_CREATE_ACTIONS_IN_SPLIT_VIEW) === 'true',

		workspaceTabsEnabled: resolveWorkspaceTabsEnabled(),

		workspaceTabsPosition: resolveWorkspaceTabsPosition(localStorage.getItem(WORKSPACE_TABS_POSITION)),

		workspaceTabsRecordsInSplitView: localStorage.getItem(WORKSPACE_TABS_RECORDS_IN_SPLIT_VIEW) === 'true',
	};
}

export default createDefaultUserInterfaceSettingsState;
